import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getGmailHealth, gmailSync, gmailAutoPoll } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AlertTriangle, AlertOctagon, RefreshCw, Loader2, Unplug, Play, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";

export const GmailHealthBanner = ({ compact = false, onChange }) => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const load = useCallback(() => getGmailHealth().then((h) => { setHealth(h); onChangeRef.current?.(h); return h; }), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!health || !["warning", "error"].includes(health.status)) return null;
  const isError = health.status === "error";
  const needsReconnect = health.issues.some((i) => i.action === "reconnect");
  const canResume = health.issues.some((i) => i.action === "resume");

  const retry = async () => {
    setBusy("retry");
    try {
      const res = await gmailSync(user);
      if (res.ok) toast.success(`Inbox check OK — ${res.processed} new invoice(s)`);
      else toast.error(res.error || "Inbox check failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inbox check failed");
    }
    await load();
    setBusy(null);
  };

  const resume = async () => {
    setBusy("resume");
    await gmailAutoPoll(true, user);
    toast.success("Automatic inbox checks resumed");
    await load();
    setBusy(null);
  };

  return (
    <div
      data-testid="gmail-health-banner"
      data-status={health.status}
      className={cn("rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3",
        isError ? "border-red-500/40 bg-red-500/5" : "border-amber-500/40 bg-amber-500/5",
        compact && "p-3 mt-4")}
    >
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", isError ? "bg-red-500/15 text-red-500" : "bg-amber-500/15 text-amber-500")}>
        {isError ? <AlertOctagon className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-2" data-testid="gmail-health-title">
          <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
          Gmail ingestion {isError ? "stopped" : "needs attention"}
          {health.email && <span className="font-mono text-xs text-muted-foreground truncate">{health.email}</span>}
        </div>
        <ul className="mt-1 space-y-0.5">
          {health.issues.map((i) => (
            <li key={i.code} data-testid={`gmail-health-issue-${i.code}`} className="text-xs text-muted-foreground">{i.message}</li>
          ))}
        </ul>
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {needsReconnect && !compact && (
          <Button data-testid="gmail-health-reconnect" size="sm" onClick={() => navigate("/upload?gmail=reconnect")}>
            <Unplug className="h-4 w-4 mr-1.5" /> Reconnect Gmail
          </Button>
        )}
        {canResume && (
          <Button data-testid="gmail-health-resume" size="sm" variant={needsReconnect ? "outline" : "default"} onClick={resume} disabled={!!busy}>
            {busy === "resume" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />} Resume auto-check
          </Button>
        )}
        <Button data-testid="gmail-health-retry" size="sm" variant="outline" onClick={retry} disabled={!!busy}>
          {busy === "retry" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Check now
        </Button>
      </div>
    </div>
  );
};
